class KokSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    if (!this.input) return;
    this.controller?.abort();
    clearTimeout(this.timer);
    this.controller = new AbortController();
    this.examples = (this.input.dataset.examples || this.input.placeholder).split('|').filter(Boolean);
    this.index = 0;
    this.deleting = true;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const options = { signal: this.controller.signal };
    const pause = () => clearTimeout(this.timer);
    const resume = () => this.schedule(1800);
    this.addEventListener('focusin', pause, options);
    this.addEventListener('focusout', resume, options);
    this.input.addEventListener('input', pause, options);
    document.addEventListener('visibilitychange', () => document.hidden ? pause() : resume(), options);
    this.motion.addEventListener('change', () => {
      pause();
      this.input.placeholder = this.examples[this.index];
      this.deleting = true;
      resume();
    }, options);
    resume();
  }
  schedule(delay) {
    clearTimeout(this.timer);
    if (!this.isConnected || this.motion.matches || document.hidden || this.contains(document.activeElement) || this.input.value || this.examples.length < 2) return;
    this.timer = setTimeout(() => this.tick(), delay);
  }
  tick() {
    const text = Array.from(this.input.placeholder);
    if (this.deleting) {
      text.pop();
      this.input.placeholder = text.join('');
      if (text.length) this.schedule(35);
      else {
        this.index = (this.index + 1) % this.examples.length;
        this.deleting = false;
        this.schedule(250);
      }
    } else {
      const example = Array.from(this.examples[this.index]);
      this.input.placeholder = example.slice(0, text.length + 1).join('');
      this.deleting = text.length + 1 >= example.length;
      this.schedule(this.deleting ? 2200 : 65);
    }
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
  }
}
class KokNavigation extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    const panels = [...this.querySelectorAll('details')];
    panels.forEach(panel => panel.addEventListener('toggle', () => {
      if (panel.open) panels.forEach(other => { if (other !== panel) other.open = false; });
    }, options));
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) panels.forEach(panel => panel.open = false);
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const open = panels.find(panel => panel.open);
      if (open) { open.open = false; open.querySelector('summary').focus(); }
    }, options);
  }
  disconnectedCallback() { this.controller.abort(); }
}
class KokQuantityOffer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.form = this.closest('form');
    this.input = this.form?.querySelector('input[name="quantity"]');
    this.selector = this.input?.closest('quantity-selector-component');
    if (!this.input) return;
    this.hidden = false;
    this.addEventListener('click', event => {
      const button = event.target.closest('button[data-quantity]');
      if (!button || button.disabled) return;
      this.input.value = button.dataset.quantity;
      this.selector?.updateButtonStates?.();
      this.selector?.onQuantityChange?.();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }, options);
    this.form.addEventListener('change', () => this.sync(), options);
    this.form.addEventListener('quantity-selector:update', () => this.sync(), options);
    this.sync();
  }
  sync() {
    const quantity = Number(this.input.value);
    const max = this.input.max ? Math.max(0, Number(this.input.max) - Number(this.input.dataset.cartQuantity || 0)) : Infinity;
    const min = Number(this.input.min || 1);
    const step = Number(this.input.step || 1);
    this.querySelectorAll('button[data-quantity]').forEach(button => {
      const amount = Number(button.dataset.quantity);
      button.setAttribute('aria-pressed', String(amount === quantity));
      button.disabled = amount > max || amount < min || (amount - min) % step !== 0 || this.input.disabled;
    });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('kok-search')) customElements.define('kok-search', KokSearch);
if (!customElements.get('kok-navigation')) customElements.define('kok-navigation', KokNavigation);
if (!customElements.get('kok-quantity-offer')) customElements.define('kok-quantity-offer', KokQuantityOffer);

class KokCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-cart-key'];
  connectedCallback() {
    this.controller = new AbortController();
    this.setup();
    this.addEventListener('click', event => {
      const button = event.target.closest('button[data-direction]');
      if (!button || button.disabled || !this.track) return;
      const index = Math.max(0, Math.min(this.track.children.length - 1, this.index + Number(button.dataset.direction)));
      const card = this.track.children[index];
      if (card) this.track.scrollTo({ left: card.offsetLeft - this.track.children[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal: this.controller.signal });
    this.dialog = this.closest('dialog');
    if (this.dialog) {
      this.openObserver = new MutationObserver(() => { if (this.dialog.open) this.setup(); });
      this.openObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }
  attributeChangedCallback() {
    if (this.isConnected) {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.setup());
    }
  }
  setup() {
    this.track = this.querySelector('.kok-cart-recs__track');
    this.pool = this.querySelector('template');
    if (!this.track || !this.pool) return;
    this.trackController?.abort();
    this.trackController = new AbortController();
    this.track.addEventListener('scroll', () => this.updateControls(), { passive: true, signal: this.trackController.signal });
    this.shuffle();
  }
  shuffle() {
    if (!this.track || !this.pool) return;
    const excluded = new Set((this.dataset.excluded || '').split(','));
    let choices = [...this.pool.content.children].filter(card => !excluded.has(card.dataset.productId));
    // Prefer a fresh set on each opening when the catalogue has enough choices.
    const fresh = choices.filter(card => !(this.previous || []).includes(card.dataset.productId));
    if (fresh.length >= 3) choices = fresh;
    for (let i = choices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [choices[i], choices[j]] = [choices[j], choices[i]];
    }
    const selected = choices.slice(0, 3);
    this.previous = selected.map(card => card.dataset.productId);
    this.track.replaceChildren(...selected.map(card => card.cloneNode(true)));
    this.hidden = selected.length === 0;
    this.track.scrollLeft = 0;
    this.index = 0;
    this.updateControls();
  }
  updateControls() {
    const cards = [...this.track.children];
    if (!cards.length) return;
    const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : this.track.clientWidth;
    this.index = step ? Math.round(this.track.scrollLeft / step) : 0;
    const label = this.querySelector('.kok-cart-recs__count');
    label.textContent = `${this.index + 1} / ${cards.length}`;
    label.setAttribute('aria-label', this.dataset.position.replace('[current]', this.index + 1).replace('[total]', cards.length));
    this.querySelector('[data-direction="-1"]').disabled = this.index === 0;
    this.querySelector('[data-direction="1"]').disabled = this.index >= cards.length - 1;
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.openObserver?.disconnect();
    this.trackController?.abort();
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('kok-cart-recommendations')) customElements.define('kok-cart-recommendations', KokCartRecommendations);

